/* eslint-disable */
import type { Money } from "../../../../common/currency.pb";
import type { RequestContext } from "../../../../franklin/RequestContextProto.pb";
import type { ResponseContext } from "../../../../franklin/ResponseContextProto.pb";
import type { Region } from "../../../../franklin/region.pb";
import type { Avatar } from "../../../../franklin/ui.pb";
import type { BitcoinAmount, CryptoAmount } from "../../../crypto/amount.pb";
import type { LimitData } from "../public.pb";
import type { LightningDeposit, LightningDepositEventType, LightningDepositInvoiceGeneration } from "./model.pb";

/**
 * Models the state of the invoice. Transitions are linear:
 * ┌────────┐       ┌─────────┐      ┌──────┐
 * │ UNPAID │──────▶│ PENDING │─────▶│ PAID │
 * └────────┘       └─────────┘      └──────┘
 *
 * Note: there is no failed state, as an invoice is payable
 * until it has expired or has been successfully paid.
 */
export enum LightningInvoiceState {
  /** STATE_UNKNOWN - Default is undefined and indicates a versioning error */
  STATE_UNKNOWN = "STATE_UNKNOWN",
  /** UNPAID - Invoice is able to be paid */
  UNPAID = "UNPAID",
  /** PENDING - Invoice is in the process of being paid */
  PENDING = "PENDING",
  /** PAID - Invoice has been paid successfully */
  PAID = "PAID",
  /** EXPIRED - Invoice has expired and is no longer payable */
  EXPIRED = "EXPIRED",
}

export interface RefreshInvoice {
}

export interface RefreshInvoiceRequest {
  /** display Id for the invoice */
  invoiceId?: string;
  /** Collection of meta-data associated with the request */
  request_context?: RequestContext;
}

export interface RefreshInvoiceResponse {
  /** Collection of meta-data associated with the response */
  response_context?: ResponseContext;
}

export interface GenerateLightningInvoice {
}

export interface GenerateLightningInvoiceRequest {
  /** The sats amount requested by the customer. In case of no amount server will assume 0 amount */
  amount?: CryptoAmount;
  /** An optional note on the lightning invoice */
  description?: string;
}

export interface GenerateLightningInvoiceResponse {
  /** Full lightning network invoice */
  payment_request?: string;
  /** BIP-21-encoded URI containing lightning invoice and bitcoin wallet address */
  unified_address?: string;
  /** Customer's deposit address where the amount requested will be deposited * */
  deposit_address?: string;
  /** The fiat amount requested by the customer */
  fiat_amount?: Money;
  /**
   * The URL that client can use to generate a QR code. It includes information about full lightning invoice and
   * bitcoin wallet address
   */
  qr_code_url?: string;
  /** The URL that the customer can use to share a lightning invoice */
  share_url?: string;
}

export interface ValidateLightningDeposit {
}

export interface ValidateLightningDepositRequest {
  payment_hash?: string;
  amount_milli_satoshis?: number;
}

export interface ValidateLightningDepositResponse {
  success?: ValidateLightningDepositSuccess | undefined;
  failed?: ValidateLightningDepositFailed | undefined;
}

export interface ValidateLightningDepositSuccess {
}

export interface ValidateLightningDepositFailed {
}

/** A request to determine whether a customer is eligible */
export interface GetEligibility {
}

export interface GetEligibilityRequest {
  /** The required customer to get limit information for */
  customer_token?: string;
  /** represents the full user agent string from the originating request. */
  user_agent?: string;
  /** test against this postcode */
  postcode?: string;
}

export interface GetEligibilityResponse {
  is_deposit_eligible?: boolean;
  is_withdrawal_eligible?: boolean;
  postcode?: string;
  region?: string;
}

/** Events sent to Snowflake for lightning deposits */
export interface LightningDepositTransition {
  /** The time in epoch millis that this event occurred */
  occurred_at?: Date;
  /** The type of event */
  type?: LightningDepositEventType;
  /** The state of the deposit immediately after the event occurred. */
  deposit?: LightningDeposit;
}

/** Events sent to Snowflake for lightning deposit invoice generation */
export interface LightningDepositInvoice {
  /** The time in epoch millis that this event occurred */
  occurred_at?: Date;
  /** The state of the deposit invoice immediately after the event occurred. */
  invoice?: LightningDepositInvoiceGeneration;
}

/** A request representing a retrieval of Crypto Lightning receive limit information for a given customer */
export interface GetCryptoLightningReceiveLimitsRequest {
  /** The required customer to get limit information for */
  customer_token?: string;
  /** represents the full user agent string from the originating request. */
  user_agent?: string;
}

/** A response representing the data about a user's crypto lightning receive limits */
export interface GetCryptoLightningReceiveLimitsResponse {
  /** The customer limit information is received for * */
  customer_token?: string;
  /** A list of limit information for different time periods, e.g weekly */
  limit_data?: LimitData[];
}

export interface GetLightningInvoiceState {
}

export interface GetLightningInvoiceStateResponse {
  /** The current state of this invoice */
  state?: LightningInvoiceState;
  /** Bitcoin amount paid by the customer, denominated in sats if invoice is paid */
  amount?: BitcoinAmount;
  /** Fiat equivalent of bitcoin amount paid by the customer if invoice is paid */
  fiat_amount?: Money;
}

export interface GetLightningInvoice {
}

export interface GetLightningInvoiceResponse {
  /** Subset of Franklin's customer profile data * */
  profile?: GetLightningInvoiceProfile;
  /** QR code image URL exposed by Macguffin, e.g. https://api.cash.app/qr/... */
  qr_code_url?: string;
  /** Full BOLT-11 lightning invoice */
  payment_request?: string;
  /** BIP-21-encoded URI containing lightning invoice and bitcoin wallet address */
  unified_address?: string;
  /** Bitcoin amount requested by the customer, denominated in sats */
  amount?: BitcoinAmount;
  /** Fiat equivalent of bitcoin amount requested by the customer */
  fiat_amount?: Money;
  /** The current state of this invoice */
  state?: LightningInvoiceState;
  /** Customer's deposit address where the amount requested will be deposited * */
  deposit_address?: string;
}

export interface GetLightningInvoiceProfile {
  /** Customer's full name */
  display_name?: string;
  /** Customer's cashtag alias, with no prefix. */
  cashtag?: string;
  /** Customer's cashtag formatted with a regional prefix (e.g $ or £) */
  formatted_cashtag?: string;
  /** Customer's avatar, with fallback initial and hex colour */
  avatar?: Avatar;
  /** Customer's region, ISO 3166-1 alpha-3 country code */
  region?: Region;
  /** Customer's verified status * */
  is_verified?: boolean;
  /** Whether the customer is a business * */
  is_business?: boolean;
}

export interface CryptoSparkyService {
  GenerateLightningInvoice(request: GenerateLightningInvoiceRequest): Promise<GenerateLightningInvoiceResponse>;
  ValidateLightningDeposit(request: ValidateLightningDepositRequest): Promise<ValidateLightningDepositResponse>;
  GetCryptoLightningReceiveLimits(
    request: GetCryptoLightningReceiveLimitsRequest,
  ): Promise<GetCryptoLightningReceiveLimitsResponse>;
  GetEligibility(request: GetEligibilityRequest): Promise<GetEligibilityResponse>;
  RefreshInvoice(request: RefreshInvoiceRequest): Promise<RefreshInvoiceResponse>;
}
